"use client";
import { MetadataProvider } from "@web/data/metadata";
import {
  Dispatch,
  FC,
  MouseEventHandler,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  MouseEvent
} from "react";

interface Item {
  wrap?: FC<{ children: ReactNode }>;
  item: ReactNode;
  ref: MutableRefObject<{}>;
}

interface FullScreenContextType {
  fullScreenItems: Item[];
  setFullScreenItems: Dispatch<SetStateAction<Item[]>>;
}

const FullScreenContext = createContext<FullScreenContextType>({
  fullScreenItems: [],
  setFullScreenItems: () => { },
});

export function FullScreen({
  children,
  wrap,
  disabled,
}: {
  children: ReactNode;
  wrap?: FC<{ children: ReactNode }>;
  disabled?: boolean;
}) {
  const { setFullScreenItems } = useContext(FullScreenContext);
  const ref = useRef({});

  const callback = useCallback(() => {
    if (disabled) {
      setFullScreenItems((items) =>
        items.filter((item) => item.ref !== item.ref),
      );
    } else {
      setFullScreenItems((items) => {
        const toAdd = { ref, item: children, wrap };
        const idx = items.findIndex((item) => ref.current !== item.ref.current);
        if (idx !== -1) {
          return [...items.slice(0, idx), toAdd, ...items.slice(idx + 1)];
        } else {
          return [...items, toAdd];
        }
      });

      return () => {
        setFullScreenItems((items) =>
          items.filter((item) => item.ref !== item.ref),
        );
      };
    }
  }, [disabled, wrap, children]);

  useEffect(() => callback(), [callback]);

  return <></>;
}

export function FullScreenPlacement() {
  const { fullScreenItems } = useContext(FullScreenContext);
  return (
    <>
      {fullScreenItems.map((fullScreenItem, idx) => {
        if (fullScreenItem.wrap) {
          return (
            <fullScreenItem.wrap key={idx}>
              {fullScreenItem.item}
            </fullScreenItem.wrap>
          );
        }
        return <div key={idx}>{fullScreenItem.item}</div>;
      })}
    </>
  );
}

export function FullScreenProvider({ children }: { children: ReactNode }) {
  const [nodes, setNodes] = useState<Item[]>([]);
  return (
    <FullScreenContext.Provider
      value={{ setFullScreenItems: setNodes, fullScreenItems: nodes }}
    >
      {children}
    </FullScreenContext.Provider>
  );
}

export function createModalWrap(
  dismiss: () => void,
  className: string = "relative top-1/2 left-1/2 w-[calc(100%-1.5rem)] sm:w-fit -translate-x-1/2 -translate-y-1/2",
) {
  const ModalWrap: FC<{ children: ReactNode }> = useCallback(({ children }) => {
    // Scroll to the top of the page when the modal is displayed
    window.scrollTo(0, 0);

    return (
      <div
        onClick={dismiss}
        className="top-0 left-0 fixed h-full w-full bg-black bg-opacity-30 z-50"
      >
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className={className}
        >
          {children}
        </div>
      </div>
    );
  }, [dismiss, className]);

  return ModalWrap;
}

export function useModalShow() {
  const [counter, setCounter] = useState(0);
  const display = useCallback((e?: MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    setCounter((counter) => counter + 1)
  }, []);
  const dismiss = useCallback(() => setCounter(0), []);
  return { showIndex: counter, display, dismiss };
}

export function AutoModal({
  showIndex,
  title,
  borderless,
  children,
}: {
  showIndex: number;
  title: ReactNode;
  borderless?: boolean;
  children: ReactNode;
}) {
  const [active, setActive] = useState(showIndex !== 0);
  useEffect(() => {
    if (showIndex) {
      setActive(true);
    } else {
      setActive(false);
    }
  }, [showIndex]);

  const dismiss = useCallback(() => {
    setActive(false);
  }, []);

  return (
    <Modal
      dismiss={dismiss}
      displayWhen={active}
      title={title}
      borderless={borderless}
    >
      {children}
    </Modal>
  );
}

export function Modal({
  dismiss,
  displayWhen,
  title,
  borderless,
  children,
}: {
  dismiss: () => void;
  displayWhen: boolean;
  title: ReactNode;
  borderless?: boolean;
  children: ReactNode;
}) {
  const modalWrap = createModalWrap(dismiss);
  const isCoverageModal =
    title === "League Coverage" || title === "Data Coverage";
  const useTransparentBorder = isCoverageModal || borderless;

  if (!displayWhen) {
    return <></>;
  }
  return (
    <FullScreen wrap={modalWrap} disabled={!displayWhen}>
      <MetadataProvider>
        <div
          className={`max-h-[90vh] overflow-auto sm:mt-10 sm:shadow-2xl sm:w-[840px] w-full flex flex-col sm:rounded-2xl bg-[#121212] text-white border ${
            useTransparentBorder ? "border-transparent" : "border-transparent"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-start px-8 pt-8 pb-4 border-b border-[#2A2A2A]">
            <div className="flex flex-col gap-1">
              <div className="text-xl font-bold">{title}</div>
              {isCoverageModal ? (
                <p className="text-sm text-[#3B82F6]">Select Sport</p>
              ) : null}
              {title === "Referral" ? (
                <p className="text-sm text-[#3B82F6]">
                  Invite people using your referral link to earn balance you can use for subscriptions or requests.
                </p>
              ) : null}
            </div>
            <button
              onClick={dismiss}
              className={`p-2 rounded-md border text-gray-300 hover:bg-[#1F1F1F] transition ${
                useTransparentBorder ? "border-transparent" : "border-transparent"
              }`}
            >
              <img
                className="w-4 h-4 filter invert"
                src="/svg/close.svg"
                alt="Close"
              />
            </button>
          </div>
  
          {/* Body */}
          <div className="overflow-auto px-8 py-6 space-y-4 text-sm sm:text-base">
            {children}
          </div>
        </div>
      </MetadataProvider>
    </FullScreen>
  );
  
}
